// IRMCardiaqueBullseye.js
import React, { useState, useMemo, useRef } from "react";
import { usePopper } from "react-popper";
import Button from "../../components/buttons/Button";
import { FaCopy, FaDownload, FaTimes, FaEye, FaEyeSlash } from "react-icons/fa";
import useNotification from "../../components/Notifications/useNotifications";
import Notification from "../../components/Notifications/Notification";
import { captureIRMBullseyeFromDOM } from '../renderStressBullseyeForExport';
import { createLogger } from "../../services/Debug";

// Segment IDs - 17 segments model
export const SEGMENT_IDS = [
  // Basal (6 segments)
  "basal_anterior",
  "basal_anterolateral", 
  "basal_inferolateral",
  "basal_inferior",
  "basal_inferoseptal",
  "basal_anteroseptal",
  // Mid (6 segments)
  "mid_anterior",
  "mid_anterolateral",
  "mid_inferolateral",
  "mid_inferior",
  "mid_inferoseptal",
  "mid_anteroseptal",
  // Apical (4 segments)
  "apical_anterior",
  "apical_lateral",
  "apical_inferior",
  "apical_septal",
  // Apex
  "apex"
];

// CINÉTIQUE - Troubles de la cinétique (Blue shades from light to dark, 0-3 scale)
const CINETIQUE_LEVELS = {
  0: { label: "Normal", color: "#dbeafe" },              // Very light blue
  1: { label: "Hypokinésie", color: "#93c5fd" },         // Light blue
  2: { label: "Akinésie", color: "#3b82f6" },            // Medium blue
  3: { label: "Dyskinésie", color: "#1e40af" }           // Dark blue
};

// VIABILITÉ - Rehaussement Tardif (RT) scale (Shades of green only)
const VIABILITE_LEVELS = {
  0: { label: "Pas de RT", color: "#4ade80" },           // Bright green
  1: { label: "RT 0-25%", color: "#22c55e" },            // Light green
  2: { label: "RT 25-50%", color: "#166534" },           // Darker green
  3: { label: "RT 50-100%", color: "#052e16" },           // Darker green
  4: { label: "RT et no flow", color: "#9ca3af" },       // Gray
  5: { label: "RT sous-épicardique", color: "#6b7280" }, // Medium gray
  6: { label: "RT médiomural", color: "#4b5563" }        // Dark gray
};

// PERFUSION - Orange to black scale
const PERFUSION_LEVELS = {
  0: { label: "Perfusion normale", color: "#fed7aa" },           // Light orange/peach
  1: { label: "Inhomogénicité", color: "#fb923c" },             // Orange
  2: { label: "Hypoperfusion sous-endocardique", color: "#ea580c" }, // Dark orange
  3: { label: "Hypoperfusion transmurale", color: "#000000" }   // Black
};

const IRM_STAGES = [
  { 
    id: "cinetique", 
    label: "Cinétique", 
    levels: CINETIQUE_LEVELS,
    defaultValue: 0,
    showNumbers: true,
    maxValue: 3
  },
  { 
    id: "viabilite", 
    label: "Fibrose", 
    levels: VIABILITE_LEVELS,
    defaultValue: 0,
    showNumbers: true,
    maxValue: 7
  },
  { 
    id: "perfusion", 
    label: "Perfusion", 
    levels: PERFUSION_LEVELS,
    defaultValue: 0,
    showNumbers: true,
    maxValue: 3
  }
];

// Helper functions
function createAllDefaultSegments(defaultValue = 0) {
  const data = {};
  SEGMENT_IDS.forEach((segmentId) => {
    data[segmentId] = defaultValue;
  });
  return data;
}

function parseIRMStructuredData(structuredData) {
  if (structuredData && typeof structuredData === 'object') {
    return {
      cinetique: structuredData.cinetique || createAllDefaultSegments(0),
      viabilite: structuredData.viabilite || createAllDefaultSegments(0),
      perfusion: structuredData.perfusion || createAllDefaultSegments(0),
      mesures: structuredData.mesures || createAllDefaultSegments(""),
      showPerfusion: structuredData.showPerfusion !== undefined ? structuredData.showPerfusion : true
    };
  }

  return {
    cinetique: createAllDefaultSegments(0),
    viabilite: createAllDefaultSegments(0),
    perfusion: createAllDefaultSegments(0),
    mesures: createAllDefaultSegments(""),
    showPerfusion: true
  };
}

/* ------------------------------------------------------------------
   ColoredNumberBullseyeDiagram Component (colored segments with numbers only)
------------------------------------------------------------------ */

export function ColoredNumberBullseyeDiagram({ 
  title = "", 
  value = {}, 
  onChange, 
  width = 260, 
  height = 260,
  levels = {},
  showNumbers = true,
  onContextMenu,
  stageId,
  selectedLevel = 0
}) {
  const segmentsDefinition = [
    // Basal (6 segments)
    { id: "basal_anterior", startAngle: -30, endAngle: 30, rIn: 115, rOut: 160 },
    { id: "basal_anterolateral", startAngle: 30, endAngle: 90, rIn: 115, rOut: 160 },
    { id: "basal_inferolateral", startAngle: 90, endAngle: 150, rIn: 115, rOut: 160 },
    { id: "basal_inferior", startAngle: 150, endAngle: 210, rIn: 115, rOut: 160 },
    { id: "basal_inferoseptal", startAngle: 210, endAngle: 270, rIn: 115, rOut: 160 },
    { id: "basal_anteroseptal", startAngle: 270, endAngle: 330, rIn: 115, rOut: 160 },
    // Mid (6 segments)
    { id: "mid_anterior", startAngle: -30, endAngle: 30, rIn: 70, rOut: 115 },
    { id: "mid_anterolateral", startAngle: 30, endAngle: 90, rIn: 70, rOut: 115 },
    { id: "mid_inferolateral", startAngle: 90, endAngle: 150, rIn: 70, rOut: 115 },
    { id: "mid_inferior", startAngle: 150, endAngle: 210, rIn: 70, rOut: 115 },
    { id: "mid_inferoseptal", startAngle: 210, endAngle: 270, rIn: 70, rOut: 115 },
    { id: "mid_anteroseptal", startAngle: 270, endAngle: 330, rIn: 70, rOut: 115 },
    // Apical (4 segments)
    { id: "apical_anterior", startAngle: -45, endAngle: 45, rIn: 25, rOut: 70 },
    { id: "apical_lateral", startAngle: 45, endAngle: 135, rIn: 25, rOut: 70 },
    { id: "apical_inferior", startAngle: 135, endAngle: 225, rIn: 25, rOut: 70 },
    { id: "apical_septal", startAngle: 225, endAngle: 315, rIn: 25, rOut: 70 },
    // Apex
    { id: "apex", rIn: 0, rOut: 25 },
  ];

  function polarToCartesian(cx, cy, r, angleDeg) {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }

  function arcPath(cx, cy, rIn, rOut, startAngle, endAngle) {
    const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;
    const p1 = polarToCartesian(cx, cy, rOut, startAngle);
    const p2 = polarToCartesian(cx, cy, rOut, endAngle);
    const p3 = polarToCartesian(cx, cy, rIn, endAngle);
    const p4 = polarToCartesian(cx, cy, rIn, startAngle);
    return [
      `M ${p1.x},${p1.y}`,
      `A ${rOut},${rOut} 0 ${largeArcFlag} 1 ${p2.x},${p2.y}`,
      `L ${p3.x},${p3.y}`,
      `A ${rIn},${rIn} 0 ${largeArcFlag} 0 ${p4.x},${p4.y}`,
      "Z",
    ].join(" ");
  }

  // Function to determine text color based on background
  function getTextColor(backgroundColor) {
    // For dark colors (black, dark blue, dark red, dark gray), use white text
    if (
      backgroundColor === "#000000" || 
      backgroundColor === "#1e3a8a" || 
      backgroundColor === "#172554" ||
      backgroundColor === "#1e40af" ||
      backgroundColor === "#3b82f6" ||
      backgroundColor === "#dc2626" ||
      backgroundColor === "#ea580c" ||
      backgroundColor === "#4b5563" ||
      backgroundColor === "#166534" || // Added from VIABILITE_LEVELS
      backgroundColor === "#052e16" ||   // Added from VIABILITE_LEVELS
      backgroundColor === "#22c55e" 
    ) {
      return "#ffffff";
    }
    // For medium colors, use white text
    if (
      backgroundColor === "#6b7280" ||
      backgroundColor === "#fb923c" ||
      backgroundColor === "#f97316" ||
      backgroundColor === "#9ca3af" || // Added from VIABILITE_LEVELS
      backgroundColor === "#4b5563"    // Already present, but also in VIABILITE_LEVELS
    ) {
      return "#ffffff";
    }
    // For light colors, use dark text
    return "#1f2937";
  }

  const cx = 200;
  const cy = 200;

  const handleSegmentClick = (segmentId) => {
    if (onChange) {
      const newData = { ...value, [segmentId]: selectedLevel };
      onChange(newData);
    }
  };

  const handleRightClick = (e) => {
    if (onContextMenu) {
      onContextMenu(e, stageId);
    }
  };

  return (
    <div style={{ textAlign: "center", position: "relative", width, height }}>
      {title && (
        <div style={{ 
          fontSize: "14px", 
          fontWeight: 600, 
          marginBottom: 8, 
          color: "#1f2937",
          textTransform: "uppercase",
          letterSpacing: "0.5px"
        }}>
          {title}
        </div>
      )}
      <svg 
        viewBox="0 0 400 400" 
        width={width} 
        height={height} 
        style={{ backgroundColor: "#ffffff", borderRadius: "8px" }}
        onContextMenu={handleRightClick}
      >
        {segmentsDefinition.map((seg) => {
          const segmentValue = value[seg.id] !== undefined ? value[seg.id] : 0;
          const levelInfo = levels[segmentValue] || levels[0];
          const textColor = getTextColor(levelInfo.color);
          
          if (seg.id === "apex") {
            return (
              <g key={seg.id}>
                <circle
                  cx={cx}
                  cy={cy}
                  r={seg.rOut}
                  fill={levelInfo.color}
                  stroke="#374151"
                  strokeWidth={1.5}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSegmentClick(seg.id)}
                />
                {showNumbers && (
                  <text 
                    x={cx} 
                    y={cy} 
                    textAnchor="middle" 
                    dominantBaseline="middle" 
                    fontSize="20" 
                    fill={textColor} 
                    style={{ fontWeight: 700 }}
                  >
                    {segmentValue}
                  </text>
                )}
              </g>
            );
          }

          const d = arcPath(cx, cy, seg.rIn, seg.rOut, seg.startAngle, seg.endAngle);
          const midAngle = (seg.startAngle + seg.endAngle) / 2;
          const midRadius = (seg.rIn + seg.rOut) / 2;
          const { x, y } = polarToCartesian(cx, cy, midRadius, midAngle);

          return (
            <g key={seg.id}>
              <path
                d={d}
                fill={levelInfo.color}
                stroke="#374151"
                strokeWidth={1.5}
                style={{ cursor: "pointer" }}
                onClick={() => handleSegmentClick(seg.id)}
              />
              {showNumbers && (
                <text 
                  x={x} 
                  y={y} 
                  textAnchor="middle" 
                  dominantBaseline="middle" 
                  fontSize="20" 
                  fill={textColor} 
                  style={{ fontWeight: 700 }}
                >
                  {segmentValue}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

/* ------------------------------------------------------------------
   MesuresBullseyeDiagram Component (editable text measurements)
------------------------------------------------------------------ */

function MesuresBullseyeDiagram({ title = "", value = {}, onChange, width = 260, height = 260 }) {
  const containerRef = React.useRef(null);
  const [editing, setEditing] = React.useState({ segmentId: null, x: 0, y: 0, value: "" });
  
  const segmentsDefinition = [
    // Basal (6 segments)
    { id: "basal_anterior", startAngle: -30, endAngle: 30, rIn: 115, rOut: 160 },
    { id: "basal_anterolateral", startAngle: 30, endAngle: 90, rIn: 115, rOut: 160 },
    { id: "basal_inferolateral", startAngle: 90, endAngle: 150, rIn: 115, rOut: 160 },
    { id: "basal_inferior", startAngle: 150, endAngle: 210, rIn: 115, rOut: 160 },
    { id: "basal_inferoseptal", startAngle: 210, endAngle: 270, rIn: 115, rOut: 160 },
    { id: "basal_anteroseptal", startAngle: 270, endAngle: 330, rIn: 115, rOut: 160 },
    // Mid (6 segments)
    { id: "mid_anterior", startAngle: -30, endAngle: 30, rIn: 70, rOut: 115 },
    { id: "mid_anterolateral", startAngle: 30, endAngle: 90, rIn: 70, rOut: 115 },
    { id: "mid_inferolateral", startAngle: 90, endAngle: 150, rIn: 70, rOut: 115 },
    { id: "mid_inferior", startAngle: 150, endAngle: 210, rIn: 70, rOut: 115 },
    { id: "mid_inferoseptal", startAngle: 210, endAngle: 270, rIn: 70, rOut: 115 },
    { id: "mid_anteroseptal", startAngle: 270, endAngle: 330, rIn: 70, rOut: 115 },
    // Apical (4 segments)
    { id: "apical_anterior", startAngle: -45, endAngle: 45, rIn: 25, rOut: 70 },
    { id: "apical_lateral", startAngle: 45, endAngle: 135, rIn: 25, rOut: 70 },
    { id: "apical_inferior", startAngle: 135, endAngle: 225, rIn: 25, rOut: 70 },
    { id: "apical_septal", startAngle: 225, endAngle: 315, rIn: 25, rOut: 70 },
    // Apex
    { id: "apex", rIn: 0, rOut: 25 },
  ];

  function polarToCartesian(cx, cy, r, angleDeg) {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }

  function arcPath(cx, cy, rIn, rOut, startAngle, endAngle) {
    const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;
    const p1 = polarToCartesian(cx, cy, rOut, startAngle);
    const p2 = polarToCartesian(cx, cy, rOut, endAngle);
    const p3 = polarToCartesian(cx, cy, rIn, endAngle);
    const p4 = polarToCartesian(cx, cy, rIn, startAngle);
    return [
      `M ${p1.x},${p1.y}`,
      `A ${rOut},${rOut} 0 ${largeArcFlag} 1 ${p2.x},${p2.y}`,
      `L ${p3.x},${p3.y}`,
      `A ${rIn},${rIn} 0 ${largeArcFlag} 0 ${p4.x},${p4.y}`,
      "Z",
    ].join(" ");
  }

  const cx = 200;
  const cy = 200;

  const openEditor = (segmentId, px, py) => {
    const current = (value && value[segmentId]) || "";
    setEditing({ segmentId, x: px, y: py, value: String(current) });
    setTimeout(() => {
      const input = containerRef.current && containerRef.current.querySelector('input[data-bullseye-input="true"]');
      if (input) input.focus();
    }, 0);
  };

  const commitEditor = () => {
    if (!editing.segmentId) return;
    const raw = (editing.value || "").trim();
    const newData = { ...(value || {}), [editing.segmentId]: raw };
    if (onChange) onChange(newData);
    setEditing({ segmentId: null, x: 0, y: 0, value: "" });
  };

  const cancelEditor = () => {
    setEditing({ segmentId: null, x: 0, y: 0, value: "" });
  };

  const handleSegmentClick = (segmentId, segmentCenter) => {
    const scaleX = width / 400;
    const scaleY = height / 400;
    const px = segmentCenter.x * scaleX;
    const py = segmentCenter.y * scaleY;
    openEditor(segmentId, px, py);
  };

  return (
    <div ref={containerRef} style={{ textAlign: "center", position: "relative", width, height }}>
      {title && (
        <div style={{ 
          fontSize: "14px", 
          fontWeight: 600, 
          marginBottom: 8, 
          color: "#1f2937",
          textTransform: "uppercase",
          letterSpacing: "0.5px"
        }}>
          {title}
        </div>
      )}
      <svg viewBox="0 0 400 400" width={width} height={height} style={{ backgroundColor: "#ffffff", borderRadius: "8px" }}>
        {segmentsDefinition.map((seg) => {
          if (seg.id === "apex") {
            const isEditing = editing.segmentId === seg.id;
            const strokeColor = isEditing ? "#2563eb" : "#6b7280";
            const strokeW = isEditing ? 2.5 : 1.5;
            return (
              <g key={seg.id}>
                <circle
                  cx={cx}
                  cy={cy}
                  r={seg.rOut}
                  fill="#ffffff"
                  stroke={strokeColor}
                  strokeWidth={strokeW}
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSegmentClick(seg.id, { x: cx, y: cy })}
                />
                {(value && value[seg.id]) ? (
                  <text 
                    x={cx} 
                    y={cy} 
                    textAnchor="middle" 
                    dominantBaseline="middle" 
                    fontSize="20" 
                    fill="#1f2937" 
                    style={{ fontWeight: 600 }}
                  >
                    {value[seg.id]}
                  </text>
                ) : null}
              </g>
            );
          }

          const d = arcPath(cx, cy, seg.rIn, seg.rOut, seg.startAngle, seg.endAngle);
          const midAngle = (seg.startAngle + seg.endAngle) / 2;
          const midRadius = (seg.rIn + seg.rOut) / 2;
          const { x, y } = polarToCartesian(cx, cy, midRadius, midAngle);
          const isEditing = editing.segmentId === seg.id;
          const strokeColor = isEditing ? "#2563eb" : "#6b7280";
          const strokeW = isEditing ? 2.5 : 1.5;

          return (
            <g key={seg.id}>
              <path
                d={d}
                fill="#ffffff"
                stroke={strokeColor}
                strokeWidth={strokeW}
                style={{ cursor: "pointer" }}
                onClick={() => handleSegmentClick(seg.id, { x, y })}
              />
              {(value && value[seg.id]) ? (
                <text 
                  x={x} 
                  y={y} 
                  textAnchor="middle" 
                  dominantBaseline="middle" 
                  fontSize="20" 
                  fill="#1f2937" 
                  style={{ fontWeight: 600 }}
                >
                  {value[seg.id]}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      {editing.segmentId && (
        <input
          data-bullseye-input="true"
          type="text"
          value={editing.value}
          onChange={(e) => setEditing((prev) => ({ ...prev, value: e.target.value }))}
          onBlur={commitEditor}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitEditor();
            if (e.key === 'Escape') cancelEditor();
          }}
          style={{
            position: "absolute",
            left: Math.max(0, Math.min(width - 100, editing.x - 30)),
            top: Math.max(0, Math.min(height - 32, editing.y - 14)),
            width: 100,
            height: 28,
            padding: "4px 8px",
            fontSize: 13,
            border: "2px solid #3b82f6",
            borderRadius: 4,
            outline: "none",
            background: "#ffffff",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            zIndex: 5
          }}
        />
      )}
    </div>
  );
}

// Main IRM Cardiaque View Component
export function IRMCardiaqueBullseyeView({ 
  anchorEl, 
  visible, 
  onClose, 
  structuredData = {}, 
  onBullseyeChange, 
  inline = false 
}) {
  const log = createLogger('IRMCardiaqueBullseyeView');
  const popperRef = useRef(null);
  const [selectedLevels, setSelectedLevels] = useState({
    cinetique: 0,
    viabilite: 0,
    perfusion: 0
  });
  const [contextMenu, setContextMenu] = useState(null);
  const { notification, showNotification, hideNotification } = useNotification();
  
  const irmData = useMemo(() => parseIRMStructuredData(structuredData), [structuredData]);

  const { styles, attributes } = usePopper(
    inline ? null : anchorEl,
    inline ? null : popperRef.current,
    {
      placement: "auto",
      modifiers: [
        { name: "offset", options: { offset: [0, 10] } },
        { name: "preventOverflow", options: { padding: 8 } },
      ],
    }
  );

  // Handle Escape key
  React.useEffect(() => {
    if (inline) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && visible) {
        onClose();
      }
    };

    if (visible) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, onClose, inline]);

  const handleStageChange = (stage, newData) => {
    const updatedData = {
      ...irmData,
      [stage]: newData
    };
    onBullseyeChange(updatedData);
  };

  const handleMesuresChange = (newData) => {
    const updatedData = {
      ...irmData,
      mesures: newData
    };
    onBullseyeChange(updatedData);
  };

  const handleTogglePerfusion = () => {
    const updatedData = {
      ...irmData,
      showPerfusion: !irmData.showPerfusion
    };
    onBullseyeChange(updatedData);
  };

  const handleContextMenu = (e, stageId) => {
    e.preventDefault();
    const stage = IRM_STAGES.find(s => s.id === stageId);
    if (!stage) return;
    
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      stageId,
      levels: stage.levels
    });
  };

  const handleContextMenuClick = (levelValue) => {
    if (!contextMenu) return;
    
    const newData = { ...irmData };
    const segments = SEGMENT_IDS;
    
    newData[contextMenu.stageId] = segments.reduce((acc, segment) => {
      acc[segment] = levelValue;
      return acc;
    }, {});
    
    onBullseyeChange(newData);
    setContextMenu(null);
  };

  // Helper function to generate legend for a specific stage
  const generateStageLegend = (stageId, stageData) => {
    const stage = IRM_STAGES.find(s => s.id === stageId);
    if (!stage) return null;

    // Get unique levels that are used
    const usedLevels = [...new Set(Object.values(stageData || {}))].sort((a, b) => a - b);

    return (
      <div style={{
        marginTop: 16,
        padding: "8px",
        backgroundColor: "#ffffff",
        borderRadius: 4,
        fontSize: 11
      }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {usedLevels.map((level) => {
            const levelInfo = stage.levels[level] || stage.levels[0];
            return (
              <div key={level} style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                padding: "2px 6px",
                backgroundColor: "#ffffff",
                marginTop: 4
              }}>
                <div style={{
                  width: 8,
                  height: 8,
                  backgroundColor: levelInfo.color,
                  border: levelInfo.color === "#000000" ? "1px solid #374151" : "1px solid transparent",
                  borderRadius: 1
                }} />
                <span style={{ color: "#475569", fontSize: 12 }}>
                  {level}: {levelInfo.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const handleCopyImage = async () => {
    try {
      const dataUrl = await captureIRMBullseyeFromDOM({ scale: 4, outputDownscaleFactor: 0.5 });
      if (!dataUrl) {
        showNotification("Erreur lors de la capture de l'image", "error");
        return;
      }
      
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const clipboardItem = new ClipboardItem({ "image/png": blob });
      await navigator.clipboard.write([clipboardItem]);
      showNotification("Image copiée avec succès", "success");
    } catch (err) {
      log.error("Erreur lors de la copie:", err);
      showNotification("Erreur lors de la copie de l'image", "error");
    }
  };

  const handleDownloadPNG = async () => {
    try {
      const dataUrl = await captureIRMBullseyeFromDOM({ scale: 4, outputDownscaleFactor: 0.5 });
      if (!dataUrl) {
        showNotification("Erreur lors de la capture de l'image", "error");
        return;
      }
      
      const link = document.createElement('a');
      link.download = `irm_cardiaque_${new Date().toISOString().slice(0, 10)}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      log.error('Error downloading image:', err);
      showNotification("Erreur lors du téléchargement", "error");
    }
  };

  if (!inline && !visible) return null;

  return (
    <div
      ref={popperRef}
      style={inline ? {
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: "20px",
        boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
        width: "100%",
        overflow: "auto",
        minWidth: "auto",
        minHeight: "auto"
      } : {
        ...styles.popper,
        zIndex: 9999,
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: "20px",
        boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
        maxWidth: "95vw",
        maxHeight: "95vh",
        overflow: "auto",
        resize: "both",
        minWidth: "1000px",
        minHeight: "500px",
      }}
      {...(!inline ? attributes.popper : {})}
      onClick={inline ? undefined : (e) => e.stopPropagation()}
    >
      {notification && (
        <Notification
          key={notification.id}
          id={notification.id}
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
        />
      )}
      
      {/* Header with actions */}
      {!inline && (
        <div style={{
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e5e7eb",
          paddingBottom: 12
        }}>
          <div style={{ display: "flex", gap: "8px" }}>
            <Button onClick={handleCopyImage} variant="secondary" icon={FaCopy}>
              Copier
            </Button>
            <Button onClick={handleDownloadPNG} variant="secondary" icon={FaDownload}>
              Télécharger
            </Button>
            <Button
              onClick={handleTogglePerfusion}
              variant="secondary"
              icon={irmData.showPerfusion ? FaEye : FaEyeSlash}
            >
              {irmData.showPerfusion ? "Masquer" : "Afficher"} Perfusion
            </Button>
            <Button onClick={onClose} variant="secondary" icon={FaTimes}>
              Fermer
            </Button>
          </div>
        </div>
      )}

      {/* Main content */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%", paddingBottom: "40px" }}>
        {/* Wrapper for image capture - bullseyes only */}
        <div id="irm-bullseye-wrapper" style={{
          display: "flex",
          flexDirection: "column",
          gap: 20,
          backgroundColor: "#fff",
          padding: "12px",
          borderRadius: "8px",
          border: "1px solid #e5e7eb",
          width: "fit-content"
        }}>
          {/* Bullseyes grid - 4x1 or 3x1 layout with legends */}
          <div style={{
            display: "grid",
            gridTemplateColumns: irmData.showPerfusion ? "repeat(4, 280px)" : "repeat(3, 280px)",
            gap: 24,
            alignItems: "start"
          }}>
            {/* Mesures */}
            <div>
              <MesuresBullseyeDiagram
                title="Epaisseur (mm)"
                value={irmData.mesures || {}}
                onChange={handleMesuresChange}
                width={280}
                height={280}
              />
            </div>

            {/* Cinétique */}
            <div>
              <ColoredNumberBullseyeDiagram
                title="Cinétique"
                value={irmData.cinetique || {}}
                onChange={(newData) => handleStageChange('cinetique', newData)}
                selectedLevel={selectedLevels.cinetique}
                width={280}
                height={280}
                levels={CINETIQUE_LEVELS}
                showNumbers={true}
                onContextMenu={handleContextMenu}
                stageId="cinetique"
              />
              {generateStageLegend('cinetique', irmData.cinetique)}
            </div>

            {/* Viabilité */}
            <div>
              <ColoredNumberBullseyeDiagram
                title="Fibrose"
                value={irmData.viabilite || {}}
                onChange={(newData) => handleStageChange('viabilite', newData)}
                selectedLevel={selectedLevels.viabilite}
                width={280}
                height={280}
                levels={VIABILITE_LEVELS}
                showNumbers={true}
                onContextMenu={handleContextMenu}
                stageId="viabilite"
              />
              {generateStageLegend('viabilite', irmData.viabilite)}
            </div>

            {/* Perfusion - conditionally rendered */}
            {irmData.showPerfusion && (
              <div>
                <ColoredNumberBullseyeDiagram
                  title="Perfusion"
                  value={irmData.perfusion || {}}
                  onChange={(newData) => handleStageChange('perfusion', newData)}
                  selectedLevel={selectedLevels.perfusion}
                  width={280}
                  height={280}
                  levels={PERFUSION_LEVELS}
                  showNumbers={true}
                  onContextMenu={handleContextMenu}
                  stageId="perfusion"
                />
                {generateStageLegend('perfusion', irmData.perfusion)}
              </div>
            )}
          </div>
        </div>

        {/* Toggle button for Perfusion visibility - below bullseyes, outside capture area */}
        <div style={{ display: "flex", justifyContent: "center", marginTop: "8px" }}>
          <Button
            onClick={handleTogglePerfusion}
            variant="secondary"
            icon={irmData.showPerfusion ? FaEye : FaEyeSlash}
            style={{ fontSize: "12px", padding: "4px 8px" }}
          >
            {irmData.showPerfusion ? "Masquer" : "Afficher"} Perfusion
          </Button>
        </div>

        {/* Control panels below bullseyes */}
        <div style={{
          display: "flex",
          flexDirection: "row",
          gap: 16,
          overflowX: "auto",
          justifyContent: "flex-start",
          flexWrap: "wrap"
        }}>
          {IRM_STAGES.filter(stage => stage.id !== 'perfusion' || irmData.showPerfusion).map((stage) => (
            <div key={stage.id} style={{
              padding: "8px",
              border: "1px solid #e5e7eb",
              borderRadius: 6,
              background: "#f9fafb",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              minWidth: "250px",
              maxWidth: "350px",
              flex: "1 1 300px"
            }}>
              <h5 style={{ 
                margin: "0 0 6px 0", 
                fontSize: "12px", 
                fontWeight: 600, 
                color: "#374151", 
                textAlign: "center",
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>
                {stage.label}
              </h5>
              <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, overflowY: "auto" }}>
                {Object.entries(stage.levels).map(([levelValue, levelInfo]) => (
                  <button
                    key={levelValue}
                    onClick={() => setSelectedLevels(prev => ({ ...prev, [stage.id]: parseInt(levelValue) }))}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 8px",
                      cursor: "pointer",
                      border: selectedLevels[stage.id] === parseInt(levelValue) ? "2px solid #3b82f6" : "1px solid #e5e7eb",
                      background: selectedLevels[stage.id] === parseInt(levelValue) ? "#eff6ff" : "#fff",
                      borderRadius: 4,
                      fontSize: 14,
                      transition: "all 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      if (selectedLevels[stage.id] !== parseInt(levelValue)) {
                        e.currentTarget.style.background = "#f3f4f6";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedLevels[stage.id] !== parseInt(levelValue)) {
                        e.currentTarget.style.background = "#fff";
                      }
                    }}
                  >
                    <div
                      style={{
                        width: 12,
                        height: 12,
                        backgroundColor: levelInfo.color,
                        border: levelInfo.color === "#000000" ? "1px solid #374151" : "1px solid transparent",
                        borderRadius: 2,
                        flexShrink: 0
                      }}
                    />
                    <span style={{ fontWeight: selectedLevels[stage.id] === parseInt(levelValue) ? 600 : 400, textAlign: "left" }}>
                      {levelValue}: {levelInfo.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          style={{
            position: "fixed",
            left: contextMenu.x,
            top: contextMenu.y,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            padding: 8,
            boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
            zIndex: 10000,
            minWidth: 180
          }}
          onMouseLeave={() => setContextMenu(null)}
        >
          <div style={{ 
            fontSize: 13, 
            fontWeight: 600, 
            marginBottom: 8, 
            color: "#374151",
            borderBottom: "1px solid #e5e7eb",
            paddingBottom: 6
          }}>
            Appliquer à tous les segments
          </div>
          {Object.entries(contextMenu.levels).map(([levelValue, levelInfo]) => (
            <button
              key={levelValue}
              onClick={() => handleContextMenuClick(parseInt(levelValue))}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
                padding: "6px 8px",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                fontSize: 12,
                textAlign: "left",
                borderRadius: 4,
                transition: "background 0.15s"
              }}
              onMouseEnter={(e) => e.target.style.background = "#f3f4f6"}
              onMouseLeave={(e) => e.target.style.background = "transparent"}
            >
              <div
                style={{
                  width: 12,
                  height: 12,
                  backgroundColor: levelInfo.color,
                  border: "1px solid #374151",
                  borderRadius: 2
                }}
              />
              <span>{levelValue}: {levelInfo.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}